import dayjs, { Dayjs } from 'dayjs';
import pluralize from 'pluralize';
import { IProjectView, ProjectPhaseType, ProjectSummaryType } from '../../api-client/autogenerated';
import { formatDate, parseDate } from '../../scripts/utils';

/** How a project's construction schedule reads: drives the status pill and bar colors. */
export type CountdownTone = 'onSchedule' | 'overdue' | 'complete' | 'notStarted';

export interface CountdownStatus {
  /** "212/480 Days", or the reason there is no countdown yet ("NTP Date Not Set", ...). */
  headline: string;
  /** The status pill, or null when the headline is a missing-data message. */
  pill: { tone: CountdownTone; label: string } | null;
  /** How full to draw the bar (0 to 1), or null for an empty track. */
  fill: number | null;
  /** Past the scheduled end with no substantial completion: drawn in red, LD shown as accrued. */
  isOverSchedule: boolean;
  /** The headline is a missing date that construction setup is where to fix. */
  needsConstructionSetup: boolean;
  /** A short explanation beside the headline (only for N/A). */
  detail: string | null;
  /** "$1,500.00 per day LD", or "$46,000.00 current LD" once over schedule. */
  ldText: string;
  endDateLabel: 'Substantial completion' | 'Scheduled end';
  /** "March 14, 2027"; null when neither a scheduled end nor a substantial completion is set. */
  endDate: string | null;
  /** Days the scheduled end was pushed out by, shown under the end date. */
  daysAdded: number;
}

const DAY_MS = 86400000;

const formatCurrency = (value: number) =>
  value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/** The phase name shown in the phase pill. */
export const getPhaseLabel = (phase?: ProjectPhaseType | null) => {
  switch (phase) {
    case ProjectPhaseType.Design:
      return 'Design';
    case ProjectPhaseType.Bidding:
      return 'Bidding';
    case ProjectPhaseType.Construction:
      return 'Construction';
    default:
      return null;
  }
};

/**
 * The countdown for one project, as the dashboard countdown card shows it. The schedule math is
 * the card's long-standing calculation, unchanged: elapsed days since NTP over the days from NTP
 * to the scheduled end (pushed out by any days added), or to substantial completion once that is
 * recorded.
 */
export const getCountdownStatus = (
  row: IProjectView,
  summary?: ProjectSummaryType,
  now: Dayjs = dayjs(),
): CountdownStatus => {
  const startDate = parseDate(row.noticeToProceedDate || row.createdOn!);
  let scheduledEndDate = row.scheduledEndDate ? parseDate(row.scheduledEndDate) : now;
  const actualEndDate = row.substantialCompletionDate
    ? parseDate(row.substantialCompletionDate)
    : null;
  const daysAdded = summary?.daysAdded || 0;
  if (daysAdded) scheduledEndDate = scheduledEndDate.add(daysAdded, 'days');

  const totalTime = (actualEndDate || scheduledEndDate).diff(startDate, 'ms');
  let elapsedTime: number;
  if (actualEndDate) {
    elapsedTime = (actualEndDate.isBefore(now) ? actualEndDate : now).diff(startDate, 'ms');
  } else {
    elapsedTime = now.diff(startDate, 'ms');
  }
  const daysToCompletion = Math.round(totalTime / DAY_MS);
  const progress = Math.floor((elapsedTime / totalTime) * daysToCompletion);
  const percentage = progress / daysToCompletion;

  const liquidatedDamagesPerDay = row.liquidatedDamagesPerDay
    ? row.liquidatedDamagesPerDay / 100
    : 0;

  const endDateLabel = actualEndDate ? 'Substantial completion' : 'Scheduled end';
  const endDate =
    actualEndDate || row.scheduledEndDate
      ? dayjs(actualEndDate || scheduledEndDate).format('MMMM DD, YYYY')
      : null;
  // The added days only explain a scheduled end; once substantial completion is the date shown,
  // they no longer describe it.
  const shownDaysAdded = !actualEndDate && row.scheduledEndDate ? daysAdded : 0;

  const message = (headline: string, extra: Partial<CountdownStatus> = {}): CountdownStatus => ({
    headline,
    pill: null,
    fill: null,
    isOverSchedule: false,
    needsConstructionSetup: false,
    detail: null,
    ldText: `${formatCurrency(liquidatedDamagesPerDay)} per day LD`,
    endDateLabel,
    endDate,
    daysAdded: shownDaysAdded,
    ...extra,
  });

  if (!row.noticeToProceedDate)
    return message('NTP Date Not Set', { needsConstructionSetup: true });
  if (!row.scheduledEndDate)
    return message('Days to Completion Not Set', { needsConstructionSetup: true });
  if (row.phase !== ProjectPhaseType.Construction)
    return message(`Construction starts ${formatDate(row.noticeToProceedDate, true)}`, {
      pill: { tone: 'notStarted', label: 'Not started' },
    });
  if (Number.isNaN(progress))
    return message('N/A', {
      detail: totalTime === 0 ? 'Start and end dates match' : null,
    });

  const isOverSchedule = percentage >= 1 && !row.substantialCompletionDate;
  let tone: CountdownTone = 'onSchedule';
  if (percentage >= 1) tone = row.substantialCompletionDate ? 'complete' : 'overdue';
  const toneLabel = { onSchedule: 'On schedule', overdue: 'Overdue', complete: 'Complete' }[
    tone as 'onSchedule' | 'overdue' | 'complete'
  ];

  const daysOverSchedule = isOverSchedule ? Math.max(now.diff(scheduledEndDate, 'day'), 0) : 0;

  return {
    headline: `${progress}/${pluralize('Day', daysToCompletion, true)}`,
    pill: { tone, label: `${toneLabel} · ${Math.round(percentage * 100)}%` },
    fill: Math.min(Math.max(percentage, 0), 1),
    isOverSchedule,
    needsConstructionSetup: false,
    detail: null,
    ldText: isOverSchedule
      ? `${formatCurrency(liquidatedDamagesPerDay * daysOverSchedule)} current LD`
      : `${formatCurrency(liquidatedDamagesPerDay)} per day LD`,
    endDateLabel,
    endDate,
    daysAdded: shownDaysAdded,
  };
};
